import axios from 'axios';
import { showApiErrorToast } from 'utils/apiErrorHandler';

// ==============================|| AXIOS CONFIG ||============================== //

const getBaseUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:4445/api';
  } else if (hostname === '31.97.207.238') {
    return 'http://31.97.207.238:4445/api';
  } else if (hostname === 'app.omdentals.com' || hostname.endsWith('.omdentals.com')) {
    return 'https://api.app.omdentals.com/api';
  }
  
  // Fallback to env var or default domain
  return import.meta.env.VITE_API_BASE_URL || 'https://api.app.omdentals.com/api';
};

export const API_BASE_URL = getBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor for Request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor for Response
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const currentPath = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
    const currentSearch = typeof window !== 'undefined' ? (window.location.search || '') : '';
    const isPublicAuthRoute = 
      currentPath.startsWith('/setup') ||
      currentPath.startsWith('/s/') ||
      currentPath.includes('/clinic-admin/setup') ||
      currentPath.includes('/clinic-admin/register') ||
      currentPath.includes('/clinic/setup') ||
      currentSearch.includes('token=') ||
      currentSearch.includes('clinic_id=') ||
      currentSearch.includes('t=') ||
      currentPath === '/login' ||
      currentPath === '/register' ||
      currentPath === '/forgot-password' ||
      currentPath === '/reset-password';

    if (error.response && error.response.status === 401) {
      if (isPublicAuthRoute || error.config?.skipAuthRedirect) {
        // Clear any stale invalid tokens without redirecting so public setup page stays accessible
        return Promise.reject(error);
      }

      // Clear storage
      ['token', 'userId', 'user', 'full_name', 'email', 'role', 'clinic_setup_completed'].forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Automatically show notification toaster for API errors and validation failures
    const isCancelled = axios.isCancel(error);
    const skipToast = error.config?.skipToast || error.config?.skipGlobalToast;
    const isRouteNotFoundProbe =
      error.response?.status === 404 &&
      String(error.response?.data?.message || '').toLowerCase().includes('route not found');

    if (!isCancelled && !skipToast && !isRouteNotFoundProbe && error.response?.status !== 401) {
      showApiErrorToast(error);
    }

    return Promise.reject(error);
  }
);

// ==============================|| CONCURRENT GET REQUEST DEDUPLICATION ||============================== //
// Shares identical in-flight GET promises across all components to eliminate concurrent duplicate API calls globally
const originalRequest = api.request.bind(api);
const inFlightGetRequests = new Map();

api.request = function (configOrUrl, maybeConfig) {
  let config = typeof configOrUrl === 'string' ? { ...(maybeConfig || {}), url: configOrUrl } : { ...configOrUrl };
  const method = String(config.method || 'get').toLowerCase();

  // Only deduplicate GET requests (idempotent/safe reads)
  if (method !== 'get' || config.skipDeduplication) {
    return originalRequest(configOrUrl, maybeConfig);
  }

  const paramKey = config.params ? JSON.stringify(config.params) : '';
  const requestKey = `${config.url || ''}?${paramKey}`;

  if (inFlightGetRequests.has(requestKey)) {
    return inFlightGetRequests.get(requestKey);
  }

  const promise = originalRequest(configOrUrl, maybeConfig).finally(() => {
    inFlightGetRequests.delete(requestKey);
  });

  inFlightGetRequests.set(requestKey, promise);
  return promise;
};

export default api;

